/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file 获取全局对象
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */

/**
 * 没有找到 global、self、window 等全局对象后，用一个空对象模拟全局对象
 */
const fallbackGlobalObject = {};

/**
 * 获取全局对象，检测顺序为：window || global || self || {}
 */
export function getGlobal(): Window | NodeJS.Global | {} {
    if (typeof window !== 'undefined') {
        return window;
    }
    if (typeof global !== 'undefined') {
        return global;
    }
    if (typeof self !== 'undefined') {
        return self;
    }
    return fallbackGlobalObject;
}

/**
 * 获取当前时间戳
 */
export function getNow() {
    return new Date().getTime();
}

/**
 * Determines if the function is native or not
 *
 * @param {function} fn Function
 *
 * @returns {boolean} True when the function is native
 */
export function isNative(fn) {
    return !!fn && fn.toString && !fn.hasOwnProperty('toString') && /\[native code\]/.test(String(fn));
}

/**
 * 路由跳转监听
 *
 * @param {function} routeChange Function
 *
 * @returns {void}
 */
export function routeHook(routeChange) {
    const history = window.history;
    //
    // History API overrides
    //
    if (typeof history.pushState === 'function') {
        history.pushState = (function (_pushState) {
            return function (state, title, url) {
                routeChange(url);
                return _pushState.apply(this, arguments);
            };
        })(history.pushState);
    }

    if (typeof history.replaceState === 'function') {
        history.replaceState = (function (_replaceState) {
            return function (state, title, toUrl) {
                const fromUrl = window.document.URL;

                // only issue route change if a nav is not in progress or the URL is changing
                if (toUrl !== fromUrl) {
                    routeChange(toUrl);
                }

                return _replaceState.apply(this, arguments);
            };
        })(history.replaceState);
    }

    // listen for hash changes
    window.addEventListener('hashchange', function (event: HashChangeEvent) {
        const url = (event || {}).newURL;
        routeChange(url);
    });

    window.addEventListener('popstate', function (event: PopStateEvent) {
        const url = window.document.URL;
        routeChange(url);
    });
}

/**
 * Checks whether given value's type is an undefined
 * {@link isUndefined}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isUndefined(wat: any): boolean {
    return wat === void 0;
}

/**
 * 生成 GUID(UUID4)，32位十六进制字符
 * @returns string
 */
export function uuid4(): string {
    const crypto = window.crypto || window['msCrypto'];

    if (!isUndefined(crypto) && crypto.getRandomValues) {
        // Use window.crypto API if available
        // eslint-disable-next-line no-undef
        const arr = new Uint16Array(8);
        crypto.getRandomValues(arr);

        // set 4 in byte 7
        arr[3] = (arr[3] & 0xfff) | 0x4000;
        // set 2 most significant bits of byte 9 to '10'
        arr[4] = (arr[4] & 0x3fff) | 0x8000;

        const pad = function (num: any) {
            let v = num.toString(16);
            while (v.length < 4) {
                v = '0' + v;
            }
            return v;
        };

        return (
            pad(arr[0]) +
            pad(arr[1]) +
            pad(arr[2]) +
            pad(arr[3]) +
            pad(arr[4]) +
            pad(arr[5]) +
            pad(arr[6]) +
            pad(arr[7])
        );
    } else {
        // http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/2117523#2117523
        return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}
